import React from 'react';

import { useChildComponent } from '@gogame/hooks-ts';
import {
  ScreenLink,
  useScreenDispatch,
  useScreenState,
  useScreenTitle,
} from '@gogame/screens';
import DataGrid from '@gogame/data-grid';

import { EditCountryGroup } from 'scenes/CountryGroups/components/CountryGroupForm';
import {
  COLUMN_ID,
  COLUMN_NAME,
  COLUMN_COUNTRIES,
  COLUMN_UPDATED,
  COLUMN_CREATED_BY,
  COLUMN_CREATED,
} from 'scenes/CountryGroups/constants';
import { useLoadCGs, useIsUpdating } from 'scenes/CountryGroups/data';
import {
  SET_TABLE_SETTINGS,
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
} from 'scenes/CountryGroups/screenReducer';
import { countryGroupsDetailsLink } from 'scenes/CountryGroups/navigation';
import CountryGroupsActions from 'scenes/CountryGroups/components/Actions';

import Toolbar from './Toolbar';

const CountryGroupsDataTable = () => {
  const [childComponent, openChildComponent] = useChildComponent();
  const [screenTitle] = useScreenTitle();
  const isUpdating = useIsUpdating();

  const dispatch = useScreenDispatch();
  const screenState = useScreenState();

  const { data, isLoading, isRefetching } = useLoadCGs(screenState);

  const handleEdit = ({ id }) => {
    openChildComponent(EditCountryGroup, { id });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading || isUpdating}
        fetching={isRefetching}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar />}
        disableColumnResize={true}
        defaultColumnWidth={150}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'text'}
          headerName={'Name'}
          width={230}
          formatter={(value, row) => (
            <ScreenLink
              link={countryGroupsDetailsLink}
              pathParams={{ id: row.id }}
            >
              {value}
            </ScreenLink>
          )}
        />
        <DataGrid.Column
          field={COLUMN_COUNTRIES}
          type={'text'}
          headerName={'Countries'}
          minWidth={400}
          flex={1}
          sortable={false}
          getValue={(value) => value.map((item) => item.name).join(', ')}
        />
        <DataGrid.Column
          field={COLUMN_UPDATED}
          headerName={'Last Updated'}
          type={'date'}
          sortable={false}
          template={'LLL'}
          timeZone={'local'}
          getValue={(_, row) => row.metadata.lastUpdatedDate}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          type={'text'}
          sortable={false}
          getValue={(_, row) => row.metadata.createdBy}
        />
        <DataGrid.Column
          field={COLUMN_CREATED}
          headerName={'Created'}
          type={'date'}
          sortable={false}
          template={'LLL'}
          timeZone={'local'}
          getValue={(_, row) => row.metadata.createdDate}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          actionsComponent={CountryGroupsActions}
          onEdit={handleEdit}
          limit={2}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};
export default CountryGroupsDataTable;
